// app/components/chart/ChartContainer.tsx
"use client";

import React, { useRef, useEffect, useState } from "react";
import {
  createChart,
  IChartApi,
  ISeriesApi,
  ColorType,
  CandlestickSeries,
} from "lightweight-charts";

interface ChartContainerProps {
  onChartReady: (chart: IChartApi, series: ISeriesApi<"Candlestick">) => void;
  headerOffset: number;
  footerOffset: number;
}

const MIN_CHART_HEIGHT = 240;
const CONTENT_VERTICAL_PADDING = 32; // account for wrapper padding plus breathing room to avoid overflow

export const ChartContainer: React.FC<ChartContainerProps> = ({
  onChartReady,
  headerOffset,
  footerOffset,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 600 }); // Default height for SSR
  const [isClient, setIsClient] = useState(false);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const disposedRef = useRef(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) return;
    disposedRef.current = false;

    const updateDimensions = () => {
      if (containerRef.current) {
        if (disposedRef.current) return;
        const windowHeight = window.innerHeight;
        const uiHeight = Math.max(
          0,
          headerOffset + footerOffset + CONTENT_VERTICAL_PADDING
        );
        const availableHeight = Math.max(
          MIN_CHART_HEIGHT,
          windowHeight - uiHeight
        );

        setDimensions({
          width: containerRef.current.clientWidth,
          height: availableHeight,
        });

        if (chartRef.current) {
          try {
            chartRef.current.applyOptions({
              width: containerRef.current.clientWidth,
              height: availableHeight,
            });
          } catch {
            chartRef.current = null;
          }
        }
      }
    };

    // Initial setup
    if (containerRef.current && !chartRef.current) {
      const uiHeight = Math.max(
        0,
        headerOffset + footerOffset + CONTENT_VERTICAL_PADDING
      );
      const initialHeight = Math.max(
        MIN_CHART_HEIGHT,
        window.innerHeight - uiHeight
      );
      chartRef.current = createChart(containerRef.current, {
        width: containerRef.current.clientWidth,
        height: initialHeight,
        layout: {
          background: { type: ColorType.Solid, color: "black" },
          textColor: "#afafaf",
        },
        grid: {
          vertLines: { color: "#101010" },
          horzLines: { color: "#101010" },
        },
        crosshair: {
          mode: 0,
        },
        rightPriceScale: {
          borderColor: "#101010",
          alignLabels: false,
        },
        timeScale: {
          borderColor: "#101010",
          timeVisible: true,
          secondsVisible: false,
        },
      });

      const candlestickSeries = chartRef.current.addSeries(CandlestickSeries, {
        upColor: "#22c55e", // Green for up candles
        downColor: "#ef4444", // Red for down candles
        borderUpColor: "#16a34a", // Darker green for borders
        borderDownColor: "#dc2626", // Darker red for borders
        wickUpColor: "#16a34a", // Darker green for wicks
        wickDownColor: "#dc2626", // Darker red for wicks
      });
      seriesRef.current = candlestickSeries;

      if (seriesRef.current) {
        onChartReady(chartRef.current, seriesRef.current);
      }
      updateDimensions();
    }

    const resizeObserver = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    window.addEventListener("resize", updateDimensions);

    return () => {
      disposedRef.current = true;
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateDimensions);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        seriesRef.current = null;
      }
    };
  }, [onChartReady, isClient, headerOffset, footerOffset]);

  const uiHeight = Math.max(
    0,
    headerOffset + footerOffset + CONTENT_VERTICAL_PADDING
  );
  const containerHeight = isClient
    ? dimensions.height ||
      Math.max(MIN_CHART_HEIGHT, window.innerHeight - uiHeight)
    : 600; // Default height for SSR

  return (
    <div
      ref={containerRef}
      className="w-full"
      style={{
        height: `${containerHeight}px`,
      }}
    />
  );
};
